import { util } from "../../util/util";
import Behaviour from "../../ui/Behaviour";
import { ewent, observe } from "../../reactive";
import { Alt1 } from "../Alt1";
import * as a1lib from "alt1";
import { lazy } from "../../Lazy";
import { Circle } from "../../math/Circle";
import { ScreenRectangle } from "../ScreenRectangle";
import { Alt1OverlayDrawCalls } from "./Alt1OverlayDrawCalls";
import { Log } from "../../util/Log";
var uuid = util.uuid;
var log = Log.log;
export class Alt1Overlay extends Behaviour {
    constructor(oneshot = undefined, heartbeater = Alt1.instance().overlays) {
        super();
        this.oneshot = oneshot;
        this.heartbeater = heartbeater;
        this.parent = null;
        this.children_bound_to_rerender = [];
        this.group_name = uuid();
        this.is_frozen = false;
        this.visible = observe(true);
        this.is_actually_visible = observe(true);
        this.overlay = new Alt1OverlayDrawCalls.Buffer([]);
        this._interactivity = lazy(() => {
            const inter = this.withSub(new Alt1Overlay.Interactivity(this));
            inter.hovered.subscribe2(() => this.rerender()).bindTo(this.lifetime_manager);
            inter.is_default_action.subscribe2(() => this.rerender()).bindTo(this.lifetime_manager);
            return inter;
        });
        this.visible.subscribe(() => this.refreshVisibility());
        this.is_actually_visible.subscribe(() => this.refresh());
        this.heartbeater.bindToPageLifetime(this);
    }
    interactivity(create_if_not_exists = true) {
        if (!create_if_not_exists && !this._interactivity.hasValue())
            return null;
        return this._interactivity.get();
    }
    setGeometry(geometry) {
        this.overlay = geometry;
        this.refresh();
    }
    refresh() {
        if (!Alt1.checkPermission(p => p.overlays))
            return;
        if (!this.isActive() && !this.oneshot)
            return;
        if (this._interactivity.hasValue()) {
            this._interactivity.get().refreshTooltip();
        }
        this.freeze();
        alt1.overLayClearGroup(this.group_name);
        if (this.isVisible()) {
            alt1.overLaySetGroup(this.group_name);
            this.overlay.playback(this.oneshot?.alive_time ?? this.heartbeater.HEARTBEAT * 1.5);
            alt1.overLaySetGroup("");
        }
        alt1.overLayRefreshGroup(this.group_name);
    }
    rerender() {
        if (this.render != Alt1Overlay.prototype.render
            || this.renderWithBuilder != Alt1Overlay.prototype.renderWithBuilder) {
            this.setGeometry(this.render());
        }
        this.children_bound_to_rerender.forEach(c => c.rerender());
    }
    render() {
        const builder = new Alt1OverlayDrawCalls.GeometryBuilder();
        this.renderWithBuilder(builder);
        return builder.buffer();
    }
    renderWithBuilder(builder) { }
    begin() {
        if (!this.oneshot) {
            this.heartbeater.onHeartbeat(() => this.refresh())
                .bindTo(this.lifetime_manager);
        }
        this.refresh();
    }
    end() {
        if (Alt1.exists()) {
            alt1.overLayClearGroup(this.group_name);
            alt1.overLayRefreshGroup(this.group_name);
        }
    }
    freeze() {
        if (this.group_name) {
            alt1.overLayFreezeGroup(this.group_name);
            this.is_frozen = true;
        }
    }
    isVisible() {
        return this.is_actually_visible.value();
    }
    setVisible(v) {
        this.visible.set(v);
        return this;
    }
    refreshVisibility() {
        this.is_actually_visible.set(this.visible.value() && (this.parent == null || this.parent.isVisible()));
    }
    addTo(parent, bind_rerender = false) {
        if (this.parent != null) {
            log().log("ERROR: Overlay already has a parent.", "Overlays");
            return this;
        }
        this.parent = parent;
        this.parent.is_actually_visible.subscribe(() => this.refreshVisibility());
        this.refreshVisibility();
        parent.withSub(this);
        if (bind_rerender)
            parent.children_bound_to_rerender.push(this);
        return this;
    }
    static oneOff(geometry, time) {
        const overlay = new Alt1Overlay({ alive_time: time });
        overlay.setGeometry(geometry);
        overlay.start();
        return overlay;
    }
    static manual(time = 3000) {
        return new Alt1Overlay({ alive_time: time }).start();
    }
}
(function (Alt1Overlay) {
    var GeometryBuilder = Alt1OverlayDrawCalls.GeometryBuilder;
    class Interactivity extends Behaviour {
        constructor(parent) {
            super();
            this.parent = parent;
            this.main_hotkey_pressed = ewent();
            this.right_clicked = ewent();
            this.bounds = null;
            this.tooltip = null;
            this.active_tooltip = null;
            this.hovered = observe(false);
            this.is_default_action = observe(false);
        }
        setBounds(bounds) {
            this.bounds = bounds;
            return this;
        }
        begin() {
            Alt1.instance().main_hotkey.subscribe(10, event => {
                if (!this.parent.isVisible())
                    return;
                if (Bounds.contains(this.bounds, event.mouse)) {
                    event.consume();
                    this.main_hotkey_pressed.trigger(this);
                }
            }).bindTo(this.lifetime_manager);
            Alt1.instance().main_hotkey.subscribe(9, event => {
                if (!this.parent.isVisible())
                    return;
                if (this.is_default_action.value()) {
                    event.consume();
                    this.main_hotkey_pressed.trigger(this);
                }
            }).bindTo(this.lifetime_manager);
            Alt1.instance().context_menu.subscribe(area => {
                if (!this.parent.isVisible())
                    return;
                const pos = a1lib.getMousePosition();
                if (Bounds.contains(this.bounds, pos))
                    this.right_clicked.trigger(this);
            }).bindTo(this.lifetime_manager);
            Alt1.instance().mouse_tracking.subscribe(pos => {
                const h = Bounds.contains(this.bounds, pos);
                this.hovered.set(h);
                if (h)
                    Interactivity.setHovered(this);
                else if (Interactivity._hovered.value() == this)
                    Interactivity.setHovered(null);
            }).bindTo(this.lifetime_manager);
        }
        end() {
            this.active_tooltip?.remove();
        }
        isHovered() {
            return this.hovered.value() && this.parent.isVisible();
        }
        makeDefaultHotkeyHandler() {
            Interactivity.setDefaultMainHotkeyHandler(this);
        }
        static setDefaultMainHotkeyHandler(overlay) {
            this._default_main_hotkey_handler.set(overlay);
        }
        static setHovered(overlay) {
            this._hovered.set(overlay);
        }
        setTooltip(tooltip) {
            this.tooltip = tooltip;
            return this;
        }
        refreshTooltip() {
            if (this.isHovered() && this.tooltip && !this.active_tooltip && alt1.rsActive) {
                this.active_tooltip = Alt1.instance().tooltips.setTooltip(this.tooltip);
            }
            else if (!this.isHovered() && this.active_tooltip) {
                this.active_tooltip.remove();
                this.active_tooltip = null;
            }
        }
    }
    Interactivity._default_main_hotkey_handler = observe(null)
        .subscribe((newValue, oldValue) => {
        if (oldValue)
            oldValue.is_default_action.set(false);
        if (newValue)
            newValue.is_default_action.set(true);
    });
    Interactivity._hovered = observe(null);
    Alt1Overlay.Interactivity = Interactivity;
    let Bounds;
    (function (Bounds) {
        function contains(self, position) {
            if (!self)
                return false;
            switch (self.type) {
                case "circle":
                    return Circle.contains(self.area, position);
                case "rectangle":
                    return ScreenRectangle.contains(self.area, position);
            }
            return false;
        }
        Bounds.contains = contains;
    })(Bounds = Alt1Overlay.Bounds || (Alt1Overlay.Bounds = {}));
    function debug(overlay, enabled, f) {
        const geometry = new GeometryBuilder();
        try {
            return f(geometry);
        }
        catch (e) {
            throw e;
        }
        finally {
            if (enabled && overlay)
                overlay().setGeometry(geometry.buffer());
        }
    }
    Alt1Overlay.debug = debug;
})(Alt1Overlay || (Alt1Overlay = {}));
